import type { ReactNode } from "react";
import { EmptyState, Tabs, type TabItem } from "@agentsaas/ui";
import { useT } from "../app/i18n";
import { useFiles } from "./FilesContext";
import { FileView } from "./FileView";
import { isDirty } from "./store";

export interface ExtraTab {
  item: TabItem;
  render(): ReactNode;
  onClose?(): void;
}

/** 主区：多 tab（已打开的文件 + 额外 tab，例如终端） */
export function EditorArea({ extra = [], activeExtra, onActivateExtra, actions }: { extra?: ExtraTab[]; activeExtra?: string | null; onActivateExtra?(id: string | null): void; actions?: ReactNode }) {
  const { t } = useT();
  const { store, files, active, activeFile } = useFiles();
  const items: TabItem[] = [
    ...files.map((f) => ({ id: `file:${f.path}`, label: f.name, icon: "file" as const, dirty: isDirty(f), closeLabel: t("fs.close_tab", { name: f.name }) })),
    ...extra.map((x) => x.item),
  ];
  const current = activeExtra ? activeExtra : active ? `file:${active}` : null;
  const onChange = (id: string) => {
    if (id.startsWith("file:")) {
      onActivateExtra?.(null);
      store.activate(id.slice(5));
    } else onActivateExtra?.(id);
  };
  const onClose = (id: string) => {
    if (id.startsWith("file:")) store.close(id.slice(5));
    else extra.find((x) => x.item.id === id)?.onClose?.();
  };
  const extraActive = activeExtra ? extra.find((x) => x.item.id === activeExtra) : undefined;
  return (
    <div className="editor-area">
      <div className="editor-area__head">
        {items.length ? <Tabs className="grow" label={t("fs.editor_tabs")} items={items} value={current} onChange={onChange} onClose={onClose} /> : <div className="grow" />}
        {actions ? <div className="editor-area__actions">{actions}</div> : null}
      </div>
      <div className="editor-area__body" role={items.length ? "tabpanel" : undefined}>
        {/* 额外 tab（终端）保持挂载，只切换可见性，避免切 tab 时断开会话 */}
        {extra.map((x) => (
          <div key={x.item.id} className="editor-area__pane" hidden={x.item.id !== extraActive?.item.id}>
            {x.render()}
          </div>
        ))}
        {extraActive ? null : activeFile ? <FileView key={activeFile.path} file={activeFile} /> : <EmptyState icon="file" title={t("fs.open_file_hint")} />}
      </div>
    </div>
  );
}
